import { useCallback, useRef, useEffect } from 'react';
import { useUserClientContext } from '@/app/clientContext';
import { useAppSelector } from '@/app/hooks';
import { type FilterState } from '@/features/filterSlice';
import { getFilterQuery, addGroupToFilter } from '@/features/filterHelpers';
import { getHeaders, withForwardedParams } from '@/features/utils';

interface UseExportQueryOptions {
  /**
   * Custom export endpoint path. If not provided, defaults to analysis export endpoint.
   * Should include the full path starting with /v1/
   */
  exportEndpoint?: string;
}

function generateRequestKey(
  exportUrl: string,
  exportedFormat: string,
  filterQuery: FilterState
) {
  // Create a unique key for this request
  return `${exportUrl}:${exportedFormat}:${JSON.stringify(getFilterQuery(filterQuery))}`;
}

/**
 * Custom hook for generating export queries with authentication and filtering
 */
export const useExportQuery = (options: UseExportQueryOptions = {}) => {
  const { token: tokenId, apiIdentity } = useUserClientContext();
  const { activeGroupName, selectedNetworkGroup } = useAppSelector(
    (state) => state.analysisMap
  );
  let filterQuery = useAppSelector((state) => state.filterQuery);
  let sendFilterBody = filterQuery.isSelected;
  if (activeGroupName) {
    // If there's an active group, ensure it's included in the filter query
    filterQuery = addGroupToFilter(
      activeGroupName,
      selectedNetworkGroup,
      filterQuery
    );
    sendFilterBody = true;
  }

  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  const downloadFileQuery = useCallback(
    (exportedFormat: string) => {
      const baseExportUrl =
        options.exportEndpoint ?? '/v1/analysis/level3/export';
      const exportUrl = withForwardedParams(baseExportUrl);

      // Cancel any existing request with the same parameters
      const requestKey = generateRequestKey(
        exportUrl,
        exportedFormat,
        filterQuery
      );
      const existingController = controllersRef.current.get(requestKey);
      if (existingController) {
        existingController.abort();
      }

      // Create new controller for this request
      const controller = new AbortController();
      controllersRef.current.set(requestKey, controller);

      const fetchOptions: RequestInit = {
        headers: getHeaders(tokenId, apiIdentity, {
          'Content-Type': 'application/json',
          Accept: exportedFormat,
        }),
        method: 'POST',
        credentials: 'include',
        body: sendFilterBody
          ? JSON.stringify(getFilterQuery(filterQuery))
          : undefined,
        signal: controller.signal,
      };

      const getExport = fetch(exportUrl, fetchOptions);

      // Clean up controller when request completes
      getExport.finally(() => {
        controllersRef.current.delete(requestKey);
      });

      return { getExport, cancel: () => controller.abort() };
    },
    [options.exportEndpoint, filterQuery, tokenId, apiIdentity, sendFilterBody]
  );

  // Cleanup on unmount
  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  return downloadFileQuery;
};
